import {
  confirmarCodigo,
  entrarComPasskey,
  listarPasskeys,
  passkeysDisponiveis,
  pedirCodigo,
  registrarPasskey,
  sair,
} from '@sheafyield/sync';
import { useMemo } from 'react';

import { banco, cliente, motor } from '../app/contexto.js';
import { useEstadoDoMotor, useSessao } from '../app/hooks.js';
import { CONTA_LOCAL, donoDaSessao } from '../dados/dono.js';
import { useConsulta } from '../dados/useConsulta.js';
import { emStandalone } from '../pwa/instalacao.js';
import { estimativa } from '../pwa/persistencia.js';
import { Conta } from './Conta.js';
import { Diagnostico } from './Diagnostico.js';
import { Escala } from './Escala.js';
import { PerfilEMeta, salvarParaIdentidade } from './PerfilEMeta.js';
import { Sincronizacao } from './Sincronizacao.js';
import { Tema } from './Tema.js';
import { lerRelatorio, salvarPerfil } from '../relatorio/dados.js';

declare const __VERSAO__: string;

const depsDoDiagnostico = {
  estimativa: () => estimativa(),
  emStandalone,
  versao: __VERSAO__,
} as const;
const donoDoApp = donoDaSessao(cliente);

export function AjustesTela() {
  const sessao = useSessao();
  const estado = useEstadoDoMotor();
  // `undefined` é a única fase em que ainda não sabemos a identidade. Só
  // depois de a sessão carregar a sentinela local pode virar dona do perfil.
  const dono =
    sessao === undefined ? undefined : (sessao?.user.id ?? CONTA_LOCAL);
  const hoje = new Date();
  const perfil = useConsulta(
    `perfil-em-ajustes:${dono ?? 'carregando'}`,
    async () => {
      if (dono === undefined) return undefined;
      return (
        await lerRelatorio(
          banco,
          dono,
          hoje.getFullYear(),
          hoje.getMonth() + 1,
          hoje,
        )
      ).perfil;
    },
  );
  // Memoizado por higiene, não por contrato: a `Conta` depende só das funções
  // que usa, então um literal novo não recarregaria a contagem — mas o objeto
  // estável poupa render de tudo o que a `Conta` monta.
  const depsDaConta = useMemo(
    () => ({
      cliente,
      sessao: sessao ?? null,
      origem: location.origin,
      pedirCodigo,
      confirmarCodigo,
      sair,
      passkeysDisponiveis,
      registrarPasskey,
      listarPasskeys,
      entrarComPasskey,
      // A preferência da trava mora na tabela `estado` do Dexie, junto da
      // sessão. Passada por aqui, e não herdada de um `carregar` anterior,
      // para o compilador cobrar a ligação: sem ela a opção mudaria na tela
      // e a escolha se perderia no próximo recarregamento.
      armazemDaTrava: banco.estado,
    }),
    [sessao],
  );
  return (
    // Sem `h1`: o cabeçalho preso da `Casca` já é o título "Ajustes" desta
    // tela, e o segundo aparecia colado no primeiro.
    <>
      <Conta deps={depsDaConta} />
      <PerfilEMeta
        perfil={perfil.dados}
        identidade={dono ?? 'carregando'}
        salvar={(identidade, entrada) =>
          salvarParaIdentidade(identidade, donoDoApp, (dono) =>
            salvarPerfil(banco, dono, entrada),
          )
        }
      />
      <Sincronizacao motor={motor} estado={estado} />
      <Tema />
      <Escala />
      <Diagnostico deps={depsDoDiagnostico} />
    </>
  );
}
